import type { SupportCenterAgendaEvent } from '../../types/supportCenterAppointments'
import type {
  SupportCenterHoliday,
  SupportCenterUserAvailability,
  SupportCenterUserUnavailability,
} from '../../types/supportCenterSpecialties'
export type SlotOccupancy = { free: number; total: number }

export type SlotOccupancyMap = Map<string, SlotOccupancy>

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

export function occupancyKey(dateYmd: string, hour: number): string {
  return `${dateYmd.slice(0, 10)}-${hour}`
}

function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

function parseScheduled(value: string): Date | null {
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T'))
  return Number.isNaN(d.getTime()) ? null : d
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

function isYmdInRange(ymd: string, startsOn: string, endsOn: string): boolean {
  const day = ymd.slice(0, 10)
  return day >= startsOn.slice(0, 10) && day <= endsOn.slice(0, 10)
}

function isUnavailableAt(
  rows: SupportCenterUserUnavailability[],
  userId: string,
  dateYmd: string,
  hour: number
): boolean {
  return rows.some((row) => {
    if (row.user_id !== userId) return false
    if (!isYmdInRange(dateYmd, row.starts_on, row.ends_on)) return false
    if (row.all_day) return true
    return row.hours.includes(hour)
  })
}

function isHolidayAt(rows: SupportCenterHoliday[], dateYmd: string, hour: number): boolean {
  const day = dateYmd.slice(0, 10)
  return rows.some((row) => {
    if (row.holiday_on.slice(0, 10) !== day) return false
    if (row.all_day) return true
    return row.hours.includes(hour)
  })
}

function worksAt(
  availability: SupportCenterUserAvailability | undefined,
  weekday: number,
  hour: number
): boolean {
  if (!availability) return false
  const byDay = availability.hours_by_weekday
  const dayHours = byDay?.[weekday]
  if (dayHours && dayHours.length > 0) {
    return dayHours.includes(hour)
  }
  // Legado: cruzar weekdays × hours quando o mapa por dia está vazio.
  if (Object.keys(byDay ?? {}).length > 0) {
    return false
  }
  if (availability.weekdays.length > 0 && !availability.weekdays.includes(weekday)) {
    return false
  }
  if (availability.hours.length === 0) return false
  return availability.hours.includes(hour)
}

/**
 * Ocupação a partir dos slots livres da IA-API (um profissional).
 * Célula livre se existir horário livre naquela hora.
 * `dayCount` default 5 (semana útil); use 1 para visão dia.
 */
export function buildOccupancyFromApiSlots(
  slots: Array<{ horario: string; scheduled_at: string }>,
  rangeStart: Date,
  hourStart: number,
  hourEnd: number,
  dayCount = 5
): SlotOccupancyMap {
  const freeHours = new Set<string>()
  for (const slot of slots) {
    const when = parseScheduled(slot.scheduled_at)
    if (!when) continue
    freeHours.add(occupancyKey(toDateKey(when), when.getHours()))
  }

  const map: SlotOccupancyMap = new Map()
  const safeStart = Math.min(hourStart, hourEnd)
  const safeEnd = Math.max(hourStart, hourEnd)
  const days = Math.max(1, dayCount)
  for (let d = 0; d < days; d++) {
    const day = addDays(rangeStart, d)
    const ymd = toDateKey(day)
    for (let hour = safeStart; hour <= safeEnd; hour++) {
      const key = occupancyKey(ymd, hour)
      map.set(key, { free: freeHours.has(key) ? 1 : 0, total: 1 })
    }
  }
  return map
}

type LocalOccupancyInput = {
  /** Início do intervalo (segunda na semana, ou o próprio dia na visão dia). */
  rangeStart: Date
  /** Quantidade de dias na grade (5 na semana útil, 1 no dia). */
  dayCount?: number
  hourStart: number
  hourEnd: number
  professionalIds: string[]
  availabilities: SupportCenterUserAvailability[]
  unavailabilities: SupportCenterUserUnavailability[]
  /** Feriados globais — bloqueiam todos os profissionais. */
  holidays?: SupportCenterHoliday[]
  events: SupportCenterAgendaEvent[]
}

/**
 * Ocupação local (filtro "Todos"): grade + indisponibilidade + feriados + agendamentos abertos.
 * Não consulta Outlook — só grade cadastrada e eventos do intervalo.
 */
export function buildOccupancyLocal(input: LocalOccupancyInput): SlotOccupancyMap {
  const {
    rangeStart,
    dayCount = 5,
    hourStart,
    hourEnd,
    professionalIds,
    availabilities,
    unavailabilities,
    holidays = [],
    events,
  } = input

  const availByUser = new Map<string, SupportCenterUserAvailability>()
  for (const row of availabilities) {
    if (!availByUser.has(row.user_id)) availByUser.set(row.user_id, row)
  }

  const openBusy = new Set<string>()
  for (const event of events) {
    if (
      event.status !== 'marcado' &&
      event.status !== 'reagendado' &&
      event.status !== 'pre_agendado'
    ) {
      continue
    }
    const userId = event.professional_user_id?.trim()
    if (!userId) continue
    const when = parseScheduled(event.scheduled_at)
    if (!when) continue
    openBusy.add(`${userId}|${occupancyKey(toDateKey(when), when.getHours())}`)
  }

  const map: SlotOccupancyMap = new Map()
  const safeStart = Math.min(hourStart, hourEnd)
  const safeEnd = Math.max(hourStart, hourEnd)
  const ids = professionalIds.filter(Boolean)
  const days = Math.max(1, dayCount)

  for (let d = 0; d < days; d++) {
    const day = addDays(rangeStart, d)
    const ymd = toDateKey(day)
    const weekday = day.getDay()
    for (let hour = safeStart; hour <= safeEnd; hour++) {
      let total = 0
      let free = 0
      const holidayBlocked = isHolidayAt(holidays, ymd, hour)
      for (const userId of ids) {
        if (holidayBlocked) continue
        if (!worksAt(availByUser.get(userId), weekday, hour)) continue
        if (isUnavailableAt(unavailabilities, userId, ymd, hour)) continue
        total += 1
        if (!openBusy.has(`${userId}|${occupancyKey(ymd, hour)}`)) {
          free += 1
        }
      }
      map.set(occupancyKey(ymd, hour), { free, total })
    }
  }
  return map
}

function weekdayFromYmd(ymd: string): number {
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay()
}

export function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + days)
  return dt.toISOString().slice(0, 10)
}

function eachYmd(fromYmd: string, toYmd: string): string[] {
  const from = fromYmd.slice(0, 10)
  const to = toYmd.slice(0, 10)
  if (!from || !to || from > to) return []
  const days: string[] = []
  let cursor = from
  while (cursor <= to && days.length < 400) {
    days.push(cursor)
    cursor = addDaysYmd(cursor, 1)
  }
  return days
}

type AgendaGridInput = {
  professionalIds: string[]
  availabilities: SupportCenterUserAvailability[]
  unavailabilities: SupportCenterUserUnavailability[]
  holidays?: SupportCenterHoliday[]
  /** `userId|yyyy-mm-dd|hora` já ocupada. */
  occupiedKeys: Set<string>
}

function gridContext(input: AgendaGridInput) {
  const availByUser = new Map<string, SupportCenterUserAvailability>()
  for (const row of input.availabilities) {
    if (!availByUser.has(row.user_id)) availByUser.set(row.user_id, row)
  }
  return {
    availByUser,
    holidays: input.holidays ?? [],
    unavailabilities: input.unavailabilities,
    ids: [...new Set(input.professionalIds.map((id) => id.trim()).filter(Boolean))],
    occupiedKeys: input.occupiedKeys,
  }
}

function userWorksHour(
  ctx: ReturnType<typeof gridContext>,
  userId: string,
  ymd: string,
  hour: number,
  holidayBlocked: boolean
): boolean {
  if (holidayBlocked) return false
  if (!worksAt(ctx.availByUser.get(userId), weekdayFromYmd(ymd), hour)) return false
  if (isUnavailableAt(ctx.unavailabilities, userId, ymd, hour)) return false
  return true
}

/** Horários da grade no intervalo (inclusive) e quantos estão ocupados. */
export function countAgendaSlots(
  input: AgendaGridInput & { dateFromYmd: string; dateToYmd: string }
): { available: number; occupied: number } {
  const ctx = gridContext(input)
  let available = 0
  let occupied = 0
  for (const ymd of eachYmd(input.dateFromYmd, input.dateToYmd)) {
    for (let hour = 0; hour <= 23; hour++) {
      const holidayBlocked = isHolidayAt(ctx.holidays, ymd, hour)
      for (const userId of ctx.ids) {
        if (!userWorksHour(ctx, userId, ymd, hour, holidayBlocked)) continue
        available += 1
        if (ctx.occupiedKeys.has(`${userId}|${ymd}|${hour}`)) occupied += 1
      }
    }
  }
  return { available, occupied }
}

/**
 * Primeiro horário livre a partir de `fromYmd`/`fromHour` (hora inclusive),
 * varrendo `dayCount` dias da grade.
 */
export function findNextFreeSlot(
  input: AgendaGridInput & { fromYmd: string; fromHour: number; dayCount: number }
): { ymd: string; hour: number } | null {
  const ctx = gridContext(input)
  const days = Math.max(0, Math.min(input.dayCount, 400))
  const startHour = Math.min(23, Math.max(0, input.fromHour))
  for (let offset = 0; offset < days; offset++) {
    const ymd = addDaysYmd(input.fromYmd, offset)
    for (let hour = offset === 0 ? startHour : 0; hour <= 23; hour++) {
      const holidayBlocked = isHolidayAt(ctx.holidays, ymd, hour)
      for (const userId of ctx.ids) {
        if (!userWorksHour(ctx, userId, ymd, hour, holidayBlocked)) continue
        if (!ctx.occupiedKeys.has(`${userId}|${ymd}|${hour}`)) {
          return { ymd, hour }
        }
      }
    }
  }
  return null
}
